import { supabase } from "@/integrations/supabase/client";
import { format, startOfQuarter, endOfQuarter } from "date-fns";

export interface QuarterlyEarnings {
  quarter: string;
  cashCompensation: number;
  noncashCompensation: number;
  totalCompensation: number;
  dealCount: number;
}

export interface TaxSummary {
  taxYear: number;
  totalCashCompensation: number;
  totalNoncashCompensation: number;
  totalCompensation: number;
  quarterlyBreakdown: QuarterlyEarnings[];
  payerCount: number;
  needs1099: boolean;
}

export async function calculateQuarterlyEarnings(
  athleteId: string,
  year: number
): Promise<QuarterlyEarnings[]> {
  const quarters = [1, 2, 3, 4];
  const results: QuarterlyEarnings[] = [];

  for (const q of quarters) {
    const quarterStart = startOfQuarter(new Date(year, (q - 1) * 3, 1));
    const quarterEnd = endOfQuarter(quarterStart);

    const { data: deals } = await supabase
      .from("deals")
      .select("amount")
      .eq("athlete_id", athleteId)
      .eq("status", "active")
      .gte("start_date", format(quarterStart, "yyyy-MM-dd"))
      .lte("start_date", format(quarterEnd, "yyyy-MM-dd"));

    const cashCompensation = deals?.reduce((sum, deal) => sum + Number(deal.amount || 0), 0) || 0;

    results.push({
      quarter: `Q${q} ${year}`,
      cashCompensation,
      noncashCompensation: 0, // TODO: Track non-cash compensation
      totalCompensation: cashCompensation,
      dealCount: deals?.length || 0,
    });
  }

  return results;
}

export async function generateTaxSummary(
  athleteId: string,
  taxYear: number
): Promise<TaxSummary> {
  const quarterlyBreakdown = await calculateQuarterlyEarnings(athleteId, taxYear);

  const totalCashCompensation = quarterlyBreakdown.reduce(
    (sum, q) => sum + q.cashCompensation,
    0
  );
  const totalNoncashCompensation = quarterlyBreakdown.reduce(
    (sum, q) => sum + q.noncashCompensation,
    0
  );

  // Get unique payers
  const { data: deals } = await supabase
    .from("deals")
    .select("brand_name")
    .eq("athlete_id", athleteId)
    .eq("status", "active")
    .gte("start_date", `${taxYear}-01-01`)
    .lte("start_date", `${taxYear}-12-31`);

  const uniquePayers = new Set(deals?.map((d) => d.brand_name) || []);

  return {
    taxYear,
    totalCashCompensation,
    totalNoncashCompensation,
    totalCompensation: totalCashCompensation + totalNoncashCompensation,
    quarterlyBreakdown,
    payerCount: uniquePayers.size,
    needs1099: totalCashCompensation >= 600, // IRS threshold
  };
}

export async function generate1099Forms(athleteId: string, taxYear: number) {
  const { data, error } = await supabase.functions.invoke("generate-1099", {
    body: { athleteId, taxYear },
  });

  if (error) throw error;
  return data;
}

export async function estimateTaxLiability(
  totalCompensation: number,
  filingStatus: "single" | "married" = "single"
): Promise<{
  federalTax: number;
  stateTax: number;
  selfEmploymentTax: number;
  totalEstimated: number;
}> {
  // Simplified tax calculation (2024 estimates)
  const selfEmploymentTax = totalCompensation * 0.153; // 15.3% SE tax
  
  // Federal income tax (simplified brackets for single filer)
  let federalTax = 0;
  if (totalCompensation > 11000) {
    federalTax = Math.min(totalCompensation - 11000, 33725) * 0.12;
  }
  if (totalCompensation > 44725) {
    federalTax += Math.min(totalCompensation - 44725, 50525) * 0.22;
  }
  if (totalCompensation > 95350) {
    federalTax += (totalCompensation - 95350) * 0.24;
  }

  const stateTax = totalCompensation * 0.05; // Simplified 5% state tax

  return {
    federalTax,
    stateTax,
    selfEmploymentTax,
    totalEstimated: federalTax + stateTax + selfEmploymentTax,
  };
}